import { useTranslations } from 'next-intl'
import { Fragment, PropsWithChildren } from 'react'

import {
  AdditionalOption,
  OldAdditionalOption,
  TRoomDetail,
} from '@/entities/room'
import { cn } from '@/shared/lib'
import { Button, Dialog, Separator } from '@/shared/ui'

import { OptionList } from './option-list'
import { useOptionList } from '../../../lib/option'
import { SubHeading } from '../../common'

type Props = Pick<TRoomDetail, 'basicOptions' | 'missingOptions'> & {
  additionalOptions: (AdditionalOption | OldAdditionalOption)[]
}
export const OptionDialogTrigger = ({
  children,
  basicOptions,
  additionalOptions,
  missingOptions,
}: PropsWithChildren<Props>) => {
  return (
    <Dialog.Root>
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      <OptionDialog
        basicOptions={basicOptions}
        additionalOptions={additionalOptions}
        missingOptions={missingOptions}
      />
    </Dialog.Root>
  )
}

export const OptionDialog = ({
  basicOptions,
  additionalOptions,
  missingOptions,
}: Props) => {
  const t = useTranslations('room_detail')

  const optionList = useOptionList({
    basicOptions,
    additionalOptions,
    missingOptions,
  })

  return (
    <Dialog.Content className="h-[760px] gap-0 p-0">
      <Dialog.Header className="p-6">
        <Dialog.Title>{t('option.modal.title')}</Dialog.Title>
        <Dialog.Description className="sr-only">
          {t('option.modal.title')}
        </Dialog.Description>
      </Dialog.Header>
      <Dialog.Body className="flex grow flex-col">
        {optionList.map(({ key, title, options }, index) => (
          <Fragment key={key}>
            <div className={cn('space-y-4 px-6')}>
              <SubHeading className="leading-6!">{title}</SubHeading>
              <OptionList
                options={options}
                disabled={key === 'missingOptions'}
                className="gap-x-15"
              />
            </div>

            {index !== optionList.length - 1 && (
              <Separator color="light-gray" size="medium" className="my-6" />
            )}
          </Fragment>
        ))}
      </Dialog.Body>

      <Dialog.Footer className="p-6">
        <Dialog.Close asChild>
          <Button size="large" color="secondary">
            {t('option.modal.button.close')}
          </Button>
        </Dialog.Close>
      </Dialog.Footer>
    </Dialog.Content>
  )
}
